import { V4_API_PREFIX } from "./api.constants";
import { IFilterOption } from "../components/shared";
import { SPLOOT_BADGE_COLOR } from "../themes/badge";
import { EarningRuleValueType, IncentiveType, PenaltyType, RuleStatus, ServiceCategory, ServiceType } from "../interfaces/earnings-setup";
import { EProductType, EProductSubType, EServiceCategory } from "./product.constants";

export const BASE_PAY_API_ENDPOINTS = {
    BASE_PAY: `/${V4_API_PREFIX}/partner-earning/rules/base-pay`,
    BASE_PAY_BY_ID: (id: string) => `/${V4_API_PREFIX}/partner-earning/rules/base-pay/${id}`,
    BASE_PAY_BULK_UPSERT_PREVIEW: `/${V4_API_PREFIX}/partner-earning/rules/base-pay/bulk-upsert/preview`,
    BASE_PAY_BULK_UPSERT: `/${V4_API_PREFIX}/partner-earning/rules/base-pay/bulk-upsert`,
    BASE_PAY_BULK_UPSERT_TEMPLATE: `/${V4_API_PREFIX}/partner-earning/rules/base-pay/bulk-upsert/template`,
} as const;

export const BASE_PAY_QUERY_KEYS = {
    BASE_PAY_RULES: "base-pay-rules",
} as const;

export enum EBasePayUploadStep {
    SELECT = "SELECT",
    PREVIEW = "PREVIEW",
    CONFIRM = "CONFIRM",
    RESULT = "RESULT",
}

export const BASE_PAY_UPLOAD_DIALOG_TITLES: Record<EBasePayUploadStep, string> = {
    [EBasePayUploadStep.SELECT]: "Upload Base Pay Rules",
    [EBasePayUploadStep.PREVIEW]: "Preview Changes",
    [EBasePayUploadStep.CONFIRM]: "Confirm Import",
    [EBasePayUploadStep.RESULT]: "Import Complete",
};

export const BASE_PAY_UPLOAD_CONSTANTS = {
    MAX_FILE_SIZE_BYTES: 10 * 1024 * 1024,
    ACCEPTED_FILE_EXTENSION: ".csv",
    ACCEPTED_MIME_TYPES: ["text/csv", "application/vnd.ms-excel"],
    TEMPLATE_FILE_NAME: "base-pay-bulk-upsert-template.csv",
} as const;

export const INCENTIVES_API_ENDPOINTS = {
    INCENTIVES: `/${V4_API_PREFIX}/partner-earning/rules/incentive`,
    INCENTIVE_BY_ID: (id: string) => `/${V4_API_PREFIX}/partner-earning/rules/incentive/${id}`,
} as const;

export const INCENTIVES_QUERY_KEYS = {
    INCENTIVE_RULES: "incentive-rules",
} as const;

export const PENALTIES_API_ENDPOINTS = {
    PENALTIES: `/${V4_API_PREFIX}/partner-earning/rules/penalty`,
    PENALTY_BY_ID: (id: string) => `/${V4_API_PREFIX}/partner-earning/rules/penalty/${id}`,
} as const;

export const PENALTIES_QUERY_KEYS = {
    PENALTY_RULES: "penalty-rules",
} as const;

export const SELECT_PLACEHOLDER = "Select"
export const LOADING_PLACEHOLDER = "Loading..."
export const CHOOSE_CITY_PLACEHOLDER = "Choose a city"
export const CURSOR_DEFAULT = "cursor-default"
export const CURSOR_POINTER = "cursor-pointer"

export interface ISelectOption {
    value: string;
    label: string;
}

export const EARNINGS_SERVICE_TYPE_OPTIONS: ISelectOption[] = [
    { value: ServiceType.SUBSCRIPTION, label: "Subscription" },
    { value: ServiceType.ONE_TIME, label: "One Time" },
];

export const EARNINGS_SERVICE_CATEGORY_OPTIONS: ISelectOption[] = [
    { value: ServiceCategory.WALKING, label: "Walking" },
    { value: ServiceCategory.GROOMING, label: "Grooming" },
];

export enum Status {
    ACTIVE = "Active",
    UPCOMING = "Upcoming",
    EXPIRED = "Expired",
    DRAFT = "Draft",
    SCHEDULED = "Scheduled",
    INACTIVE = "Inactive",
}

export const EARNINGS_STATUS_BADGE_COLORS: Record<Status, SPLOOT_BADGE_COLOR> = {
    [Status.ACTIVE]: SPLOOT_BADGE_COLOR.GREEN,
    [Status.UPCOMING]: SPLOOT_BADGE_COLOR.BLUE,
    [Status.EXPIRED]: SPLOOT_BADGE_COLOR.LIGHT_GRAY,
    [Status.DRAFT]: SPLOOT_BADGE_COLOR.GRAY,
    [Status.SCHEDULED]: SPLOOT_BADGE_COLOR.YELLOW,
    [Status.INACTIVE]: SPLOOT_BADGE_COLOR.RED
};

export enum EARNINGS_SETUP_FILTER_OPTIONS_ID {
    ALL_CATEGORIES = "all-categories",
    WALKING = "walking",
    GROOMING = "grooming",
    ALL_STATUSES = "all-statuses",
    ACTIVE = "active",
    UPCOMING = "upcoming",
    EXPIRED = "expired",
    DRAFT = "draft",
    INACTIVE = "inactive",
}

export const EARNINGS_SETUP_FILTER_OPTIONS: IFilterOption[] = [
    { id: EARNINGS_SETUP_FILTER_OPTIONS_ID.ALL_CATEGORIES, label: "All Categories", default: true },
    { id: EARNINGS_SETUP_FILTER_OPTIONS_ID.WALKING, label: "Walking" },
    { id: EARNINGS_SETUP_FILTER_OPTIONS_ID.GROOMING, label: "Grooming" },
    { id: EARNINGS_SETUP_FILTER_OPTIONS_ID.ALL_STATUSES, label: "All Statuses", default: true },
    { id: EARNINGS_SETUP_FILTER_OPTIONS_ID.ACTIVE, label: Status.ACTIVE },
    { id: EARNINGS_SETUP_FILTER_OPTIONS_ID.UPCOMING, label: Status.UPCOMING },
    { id: EARNINGS_SETUP_FILTER_OPTIONS_ID.EXPIRED, label: Status.EXPIRED },
    { id: EARNINGS_SETUP_FILTER_OPTIONS_ID.DRAFT, label: Status.DRAFT },
    { id: EARNINGS_SETUP_FILTER_OPTIONS_ID.INACTIVE, label: Status.INACTIVE }
];

export enum LevelType {
    COUNTRY_WIDE = "country-wide",
    CITY_WIDE = "city-wide",
    SUB_CITY = "sub-city",
    HUB_SPECIFIC = "hub-specific"
}

export const AMOUNT_CALCULATION_OPTIONS: ISelectOption[] = [
    { value: EarningRuleValueType.PERCENTAGE, label: "Percentage (%)" },
    { value: EarningRuleValueType.FLAT, label: "Flat (₹)" },
];

export const STATUS_OPTIONS: ISelectOption[] = [
    { value: RuleStatus.DRAFT, label: "Draft" },
    { value: RuleStatus.ACTIVE, label: "Active" },
    { value: RuleStatus.INACTIVE, label: "Inactive" },
]

export const INCENTIVE_TYPE_OPTIONS = [
    { value: IncentiveType.SUNDAY_SHIFT, label: "Sunday Shift" },
    { value: IncentiveType.ON_TIME_START, label: "On-Time Start" },
    { value: IncentiveType.COMPLETION_BONUS, label: "Completion Bonus" },
    { value: IncentiveType.RATING_SESSION, label: "Rating Session" },
    { value: IncentiveType.RATING_AGGREGATE, label: "Rating Aggregate" },
    { value: IncentiveType.FESTIVE, label: "Festive" },
    { value: IncentiveType.TIME_SLOT, label: "Time Slot" },
    { value: IncentiveType.DISTANCE, label: "Distance" },
    { value: IncentiveType.TIP, label: "Tip" },
    { value: IncentiveType.TRIAL_CONVERSION, label: "Trial Conversion" },
    { value: IncentiveType.ONLINE_DAILY, label: "Online Daily" },
    { value: IncentiveType.PERFORMANCE, label: "Performance" },
    { value: IncentiveType.FIRST_SESSION, label: "First Session" },
    { value: IncentiveType.SESSION_COUNT, label: "Weekly Session Count" },
]

export const INCENTIVE_TYPE_DESCRIPTIONS: Record<string, string> = {
    [IncentiveType.SUNDAY_SHIFT]: "Reward walkers or groomers who work on Sundays. Set a threshold (like 1 or higher) and a flat or percentage-based reward to encourage Sunday shifts.",
    [IncentiveType.ON_TIME_START]: "Reward walkers or groomers who start their sessions on time. Set a threshold (like 95% on-time starts in a month) and a flat or percentage-based reward to encourage on-time starts.",
    [IncentiveType.COMPLETION_BONUS]: "Reward walkers or groomers who complete a certain percentage of sessions. Set a threshold (like 90% sessions completed in a month) and a flat or percentage-based reward to encourage sessions completion.",
    [IncentiveType.RATING_AGGREGATE]: "Reward walkers or groomers who have an average rating of 4.5 or higher in a month. Set a threshold (like 4.5 or higher) and a flat or percentage-based reward to encourage good ratings.",
    [IncentiveType.RATING_SESSION]: "Reward walkers or groomers who have a single 5-star session in a month. Set a threshold (like 1 or higher) and a flat or percentage-based reward to encourage single 5-star sessions.",
    [IncentiveType.DISTANCE]: "Reward walkers or groomers who travel a certain distance. Set a threshold (like 5 km or higher) and a flat or percentage-based reward to encourage distance travel.",
    [IncentiveType.TIP]: "Reward walkers or groomers who tip a certain amount. Set a threshold (like 100 or higher) and a flat or percentage-based reward to encourage tipping.",
    [IncentiveType.TRIAL_CONVERSION]: "Reward walkers or groomers who convert a trial to a full session. Set a threshold (like 1 or higher) and a flat or percentage-based reward to encourage trial conversion.",
    [IncentiveType.FESTIVE]: "Reward walkers or groomers who work on festivals. Set a threshold (like 1 or higher) and a flat or percentage-based reward to encourage festival work.",
    [IncentiveType.TIME_SLOT]: "Reward walkers or groomers who work in a certain time slot. Set a threshold (like 1 or higher) and a flat or percentage-based reward to encourage time slot work.",
    [IncentiveType.PERFORMANCE]: "Reward walkers or groomers who have a certain performance. Set a threshold (like 1 or higher) and a flat or percentage-based reward to encourage performance.",
    [IncentiveType.ONLINE_DAILY]: "Reward walkers or groomers who start their sessions within the allowed window. Set a threshold (like 95% on-time starts in a month) and a flat or percentage-based reward to encourage punctuality.",
    [IncentiveType.FIRST_SESSION]: "Reward a partner when they complete their Nth session (defaults to their very first session) for this service category. Set the session number and a flat or percentage-based reward to encourage onboarding milestones.",
    [IncentiveType.SESSION_COUNT]: "Reward walkers or groomers who complete a minimum number of sessions within a week. Set the minimum (and optional maximum) session count and a flat or percentage-based reward to encourage weekly session volume.",
}

export const PENALTY_TYPE_OPTIONS = [
    { value: PenaltyType.NO_SHOW, label: "No Show" },
    { value: PenaltyType.CANCELLATION, label: "Cancellation" },
    { value: PenaltyType.LATE_LEAVE, label: "Late Leave" },
    { value: PenaltyType.RESCHEDULE, label: "Reschedule" },
    { value: PenaltyType.UNIFORM_VIOLATION, label: "Uniform Violation" },
    { value: PenaltyType.QUALITY_ISSUE, label: "Quality Issue" },
    { value: PenaltyType.CUSTOMER_DROPOUT, label: "Customer Dropout" },
]

export const PENALTY_TYPE_DESCRIPTIONS: Record<PenaltyType, string> = {
    [PenaltyType.NO_SHOW]: "Penalty for not showing up to a scheduled session. Set a threshold and a flat or percentage-based penalty.",
    [PenaltyType.CANCELLATION]: "Penalty for canceling a session. Set a threshold (like hours before cancellation) and a flat or percentage-based penalty.",
    [PenaltyType.LATE_LEAVE]: "Penalty for leaving a session late. Set a threshold and a flat or percentage-based penalty.",
    [PenaltyType.RESCHEDULE]: "Penalty for rescheduling sessions. Set a threshold and a flat or percentage-based penalty.",
    [PenaltyType.UNIFORM_VIOLATION]: "Penalty for uniform violations. Set a threshold and a flat or percentage-based penalty.",
    [PenaltyType.QUALITY_ISSUE]: "Penalty for quality issues. Set a threshold (like rating below 4.0) and a flat or percentage-based penalty.",
    [PenaltyType.CUSTOMER_DROPOUT]: "Penalty for customer dropouts. Set a threshold and a flat or percentage-based penalty.",
}

export const CATEGORY_MAP: Record<ServiceCategory, EServiceCategory> = {
    [ServiceCategory.WALKING]: EServiceCategory.WALKING,
    [ServiceCategory.GROOMING]: EServiceCategory.GROOMING,
    [ServiceCategory.PRODUCTS]: EServiceCategory.PRODUCTS,
}

export const SERVICE_CATEGORY_TO_PRODUCT_TYPE_MAP: Record<ServiceCategory, EProductType> = {
    [ServiceCategory.WALKING]: EProductType.WALKING,
    [ServiceCategory.GROOMING]: EProductType.GROOMING,
    [ServiceCategory.PRODUCTS]: EProductType.PRODUCTS,
}

export const SERVICE_TYPE_TO_PRODUCT_SUB_TYPE_MAP: Record<ServiceType, EProductSubType> = {
    [ServiceType.SUBSCRIPTION]: EProductSubType.SUBSCRIPTION,
    [ServiceType.ONE_TIME]: EProductSubType.ONE_TIME,
}